import { useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { VoiceAssistantProvider } from "@/hooks/useVoiceAssistant";
import { ScrollToTop } from "./components/ScrollToTop";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { RootLayout } from "@/components/layout/RootLayout";
import { WebVitalsReporter } from "@/components/analytics/WebVitalsReporter";

import Index from "./pages/Index";

// AI LAZY (non-critical)
const AIAgentModal = lazy(() =>
  import("@/components/ai").then((mod) => ({ default: mod.AIAgentModal }))
);

// Pages
const Treatments = lazy(() => import("./pages/Treatments"));
const TreatmentDetail = lazy(() => import("./pages/TreatmentDetail"));
const Hospitals = lazy(() => import("./pages/Hospitals"));
const Doctors = lazy(() => import("./pages/Doctors"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const MedicalTourismGuide = lazy(() => import("./pages/MedicalTourismGuide"));
const CompareCosts = lazy(() => import("./pages/CompareCosts"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

function AppContent() {
  useEffect(() => {
    if (import.meta.env.PROD) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event("render-event"));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      {/* Core Web Vitals Monitoring */}
      <WebVitalsReporter />
      
      <Toaster />
      <Sonner />

      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={null}>
          <AIAgentModal />
        </Suspense>

        <ScrollToTop />

        <Routes>
          <Route element={<RootLayout />}>

            <Route path="/" element={<Index />} />

            <Route path="/treatments/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Treatments />
              </Suspense>
            }/>

            <Route path="/treatments/:category/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Treatments />
              </Suspense>
            }/>

            <Route path="/treatments/:category/:slug/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TreatmentDetail />
              </Suspense>
            }/>

            <Route path="/hospitals/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Hospitals />
              </Suspense>
            }/>

            <Route path="/hospitals/:slug/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Hospitals />
              </Suspense>
            }/>

            <Route path="/doctors/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Doctors />
              </Suspense>
            }/>

            <Route path="/doctors/:slug/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Doctors />
              </Suspense>
            }/>

            <Route path="/blog/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Blog />
              </Suspense>
            }/>

            <Route path="/blog/category/:category/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Blog />
              </Suspense>
            }/>

            <Route path="/blog/:slug/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <BlogPost />
              </Suspense>
            }/>

            <Route path="/contact/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Contact />
              </Suspense>
            }/>

            <Route path="/medical-tourism-india/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <MedicalTourismGuide />
              </Suspense>
            }/>

            <Route path="/compare-costs/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <CompareCosts />
              </Suspense>
            }/>

            <Route path="/privacy-policy/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PrivacyPolicy />
              </Suspense>
            }/>

            <Route path="/terms-of-service/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TermsOfService />
              </Suspense>
            }/>

            <Route path="*" element={
              <Suspense fallback={<LoadingSpinner />}>
                <NotFound />
              </Suspense>
            }/>

          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <VoiceAssistantProvider>
            <AppContent />
          </VoiceAssistantProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
